import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Package, Truck, CheckCircle2, AlertTriangle, Filter, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Pagination } from '@/components/common/navigation/Pagination'
import { LoadingSpinner } from '@/components/common/feedback/LoadingSpinner'
import { FulfillmentTable } from '../components'
import { useFulfillmentOrders, useFulfillmentSummary } from '../hooks/useFulfillment'
import type { FulfillmentOrder } from '../api/fulfillmentApi'

const PAGE_SIZE = 20

const STATUS_OPTIONS = [
  'DRAFT', 'SUBMITTED', 'IN_PROGRESS', 'ON_HOLD', 'COMPLETE', 'CANCELLED', 'ERROR',
]

export function FulfillmentPage() {
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')

  const { data: summary } = useFulfillmentSummary()
  const { data, isLoading } = useFulfillmentOrders({
    start: (page - 1) * PAGE_SIZE,
    limit: PAGE_SIZE,
    status: statusFilter || undefined,
    search: search || undefined,
  })

  const orders = data?.data || []
  const total = data?.total || 0

  const submitSearch = () => {
    setPage(1)
    setSearch(searchInput.trim())
  }

  const cards = [
    { label: 'Total Orders', value: summary?.total ?? '—', icon: Package },
    { label: 'In Flight', value: summary?.in_flight ?? '—', icon: Truck },
    { label: 'Complete', value: summary?.complete ?? '—', icon: CheckCircle2 },
    { label: 'Cancelled / Error', value: summary?.problem ?? '—', icon: AlertTriangle },
  ]

  return (
    <div className="space-y-8 p-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-3xl font-semibold tracking-tight">Fulfillment</h1>
          <p className="text-muted-foreground">
            Print fulfillment orders across providers{summary?.providers?.length ? ` (${summary.providers.join(', ')})` : ''}
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-5 md:grid-cols-4">
        {cards.map((c) => (
          <Card key={c.label}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{c.label}</CardTitle>
              <c.icon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{c.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Filters:</span>
        </div>
        <Select value={statusFilter || 'all'} onValueChange={(v) => { setStatusFilter(v === 'all' ? '' : v); setPage(1) }}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {STATUS_OPTIONS.map((s) => (
              <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2">
          <Input
            placeholder="Search order id / Shopify #"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') submitSearch() }}
            className="w-[260px]"
          />
          <Button variant="secondary" size="sm" onClick={submitSearch}>
            <Search className="h-4 w-4" />
          </Button>
        </div>
        {(statusFilter || search) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { setStatusFilter(''); setSearch(''); setSearchInput(''); setPage(1) }}
          >
            Clear Filters
          </Button>
        )}
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex justify-center py-12"><LoadingSpinner /></div>
      ) : (
        <>
          <FulfillmentTable
            orders={orders}
            onRowClick={(o: FulfillmentOrder) => navigate(`/app/fulfillment/${o.id}`)}
          />
          {total > PAGE_SIZE && (
            <Pagination
              page={page}
              pageSize={PAGE_SIZE}
              total={total}
              onPageChange={setPage}
            />
          )}
        </>
      )}
    </div>
  )
}
